import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import type { TrafficLightBlockInfo } from '@/lib/trafficLightBlock';

export interface AppointmentRow {
  id: string;
  client_id: string;
  property_id: string | null;
  agent_id: string | null;
  appointment_type: 'aufnahme' | 'besichtigung' | 'kaufanbot' | 'notar' | 'sonstiges';
  archived_at: number | null;
  appointment_date: number;
  status:
    | 'open'
    | 'pending'
    | 'confirmed'
    | 'completed'
    | 'cancelled'
    | 'rejected'
    | 'signed_contract';
  notes: string | null;
  created_by: string;
  created_at: number;
  updated_at: number;
  // joined from clients + profiles (since 0027). May be null for
  // orphaned rows or appointments without an assigned agent.
  phone?: string | null;
  client_last_name?: string | null;
  client_first_name?: string | null;
  agent_full_name?: string | null;
  agent_id_short?: string | null;
  agent_avatar_key?: string | null;
  /** Derived from appointment_date + status + 30-day reservation window. */
  traffic_light?: 'off' | 'yellow' | 'green' | 'red' | 'none';
}

export interface AppointmentFilters {
  status?: AppointmentRow['status'];
  type?: AppointmentRow['appointment_type'];
  agentId?: string;
  clientId?: string;
  propertyId?: string;
  from?: number;
  to?: number;
  limit?: number;
  offset?: number;
  order?: 'asc' | 'desc';
  /** v2 — column to sort by. Default 'appointment_date' for legacy
   *  callers; /appointments uses 'created_at' so most recently
   *  scheduled appointments float to the top. */
  orderBy?: 'appointment_date' | 'created_at';
  /** When combined with `agentId == own profile.id`, also returns
   *  appointments whose owner shared their calendar with me. Drives
   *  the "Meine + geteilte" scope option on /termine. */
  includeShared?: boolean;
  /** Batch ZO #6 — for CC users, "Meine" means "rows I created". */
  createdByUserId?: string;
}

export const appointmentsKey = (filters: AppointmentFilters = {}) =>
  ['appointments', filters] as const;

export function useAppointments(
  filters: AppointmentFilters = {},
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: appointmentsKey(filters),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.status) params.set('status', filters.status);
      if (filters.type) params.set('type', filters.type);
      if (filters.agentId) params.set('agent_id', filters.agentId);
      if (filters.clientId) params.set('client_id', filters.clientId);
      if (filters.propertyId) params.set('property_id', filters.propertyId);
      if (filters.from) params.set('from', String(filters.from));
      if (filters.to) params.set('to', String(filters.to));
      if (filters.limit) params.set('limit', String(filters.limit));
      if (filters.offset) params.set('offset', String(filters.offset));
      if (filters.orderBy) params.set('order_by', filters.orderBy);
      if (filters.includeShared) params.set('include_shared', 'true');
      if (filters.createdByUserId) params.set('created_by_user_id', filters.createdByUserId);
      params.set('order', filters.order ?? 'asc');
      const res = await apiFetch(`/appointments?${params}`);
      if (!res.ok) throw new Error(`appointments_list_failed_${res.status}`);
      const json = (await res.json()) as { items: AppointmentRow[] };
      return json.items;
    },
    staleTime: 30_000,
    enabled: options.enabled ?? true,
  });
}

// Batch AH faza 2 — infinite scroll variant. Page size defaults to 50;
// stops when last page returns fewer rows than requested.
export function useInfiniteAppointments(
  filters: Omit<AppointmentFilters, 'offset' | 'limit'> = {},
  options: { enabled?: boolean; pageSize?: number } = {},
) {
  const pageSize = options.pageSize ?? 50;
  return useInfiniteQuery({
    queryKey: ['appointments', 'infinite', { ...filters, pageSize }] as const,
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams();
      if (filters.status) params.set('status', filters.status);
      if (filters.type) params.set('type', filters.type);
      if (filters.agentId) params.set('agent_id', filters.agentId);
      if (filters.clientId) params.set('client_id', filters.clientId);
      if (filters.propertyId) params.set('property_id', filters.propertyId);
      if (filters.from) params.set('from', String(filters.from));
      if (filters.to) params.set('to', String(filters.to));
      if (filters.orderBy) params.set('order_by', filters.orderBy);
      if (filters.includeShared) params.set('include_shared', 'true');
      if (filters.createdByUserId)
        params.set('created_by_user_id', filters.createdByUserId);
      params.set('order', filters.order ?? 'asc');
      params.set('limit', String(pageSize));
      params.set('offset', String(pageParam ?? 0));
      const res = await apiFetch(`/appointments?${params}`);
      if (!res.ok) throw new Error(`appointments_list_failed_${res.status}`);
      const json = (await res.json()) as { items: AppointmentRow[] };
      return { items: json.items, offset: (pageParam ?? 0) as number };
    },
    initialPageParam: 0 as number,
    getNextPageParam: (lastPage) =>
      lastPage.items.length < pageSize
        ? undefined
        : lastPage.offset + pageSize,
    staleTime: 30_000,
    enabled: options.enabled ?? true,
  });
}

export interface CreateAppointmentPayload {
  clientId: string;
  propertyId?: string | null;
  agentProfileId?: string | null;
  appointmentType: AppointmentRow['appointment_type'];
  appointmentDate: number;
  notes?: string | null;
  additionalAgentIds?: string[];
  /** Admin-only — bypass 30-day Aufnahme client lock. Audited server-side. */
  overrideAufnahmeLock?: boolean;
  /** Admin-only — bypass the "property already notariell verkauft" gate.
   *  Audited server-side. */
  overridePropertySold?: boolean;
  /** Retroactive backfill — bypass the past-date guard; row created
   *  already in 'completed' status since the event happened before. */
  retroactive?: boolean;
}

export interface AppointmentConflict {
  id: string;
  appointment_date: number;
  appointment_type: AppointmentRow['appointment_type'];
  status: AppointmentRow['status'];
  agent_name: string | null;
}

export interface AppointmentHistoryRow {
  id: string;
  action: string;
  field_changed: string | null;
  old_value: string | null;
  new_value: string | null;
  changed_by: string | null;
  automatic_change: boolean;
  change_reason: string | null;
  created_at: number;
  changed_by_full_name: string | null;
  changed_by_agent_id: string | null;
}

// Single-appointment fetch — used by the global search deep-link so a
// result can open the detail dialog even when the row isn't in the
// currently loaded page.
export function useAppointment(id: string | undefined) {
  return useQuery({
    queryKey: ['appointment', id] as const,
    queryFn: async () => {
      const res = await apiFetch(`/appointments/${id}`);
      if (!res.ok) throw new Error(`appointment_get_failed_${res.status}`);
      return (await res.json()) as AppointmentRow;
    },
    enabled: !!id,
  });
}

export function useAppointmentHistory(id: string | undefined) {
  return useQuery({
    queryKey: ['appointment', id, 'history'] as const,
    queryFn: async () => {
      const res = await apiFetch(`/appointments/${id}/history`);
      if (!res.ok) throw new Error(`appointment_history_failed_${res.status}`);
      const json = (await res.json()) as { items: AppointmentHistoryRow[] };
      return json.items;
    },
    enabled: !!id,
  });
}

export interface DryRunResult {
  dry_run: true;
  conflicts: AppointmentConflict[];
  aufnahme_lock: null | {
    lock_until: number;
    lock_agent_id: string | null;
    lock_agent_name: string | null;
  };
  /** Preuzmi-klijenta — contract-signed seller exclusive to another makler.
   *  Non-admin must cancel; admin may confirm (overrideAufnahmeLock). */
  contract_locked: null | {
    assigned_agent_id: string | null;
    assigned_agent_name: string | null;
  };
  /** Batch BE — closed seller (zatvoreno): hard block for everyone. */
  client_closed: null | { client_id: string };
  /** The existing appointment that blocks a new one for the same
   *  (client, property) pair — see lib/trafficLightBlock for the wording. */
  traffic_light_block: null | TrafficLightBlockInfo;
  /** Batch AM — surfaces when the linked property is already
   *  notariell_verkauft (or legacy `sold`). Non-admin must cancel; admin
   *  may confirm and proceed (audited). */
  property_sold: null | {
    property_id: string;
    marketing_stage: 'sold' | 'notariell_verkauft';
  };
}

export function useCreateAppointment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateAppointmentPayload) => {
      const res = await apiFetch('/appointments', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = (await res.json().catch(() => null)) as
          | { error?: string; cause?: unknown }
          | null;
        const e = new Error(err?.error ?? `appointment_create_failed_${res.status}`);
        (e as Error & { cause?: unknown }).cause = err?.cause;
        throw e;
      }
      return (await res.json()) as AppointmentRow;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['appointments'] }),
  });
}

// Pre-flight check — runs the same guards as the real submit but
// without inserting. Used by CreateAppointmentDialog to show a confirm
// dialog when a same-day conflict or Aufnahme lock is detected.
export function useDryRunAppointment() {
  return useMutation({
    mutationFn: async (payload: CreateAppointmentPayload) => {
      const res = await apiFetch('/appointments', {
        method: 'POST',
        body: JSON.stringify({ ...payload, dryRun: true }),
      });
      if (!res.ok) {
        const err = (await res.json().catch(() => null)) as
          | { error?: string; cause?: unknown }
          | null;
        const e = new Error(err?.error ?? `appointment_dryrun_failed_${res.status}`);
        (e as Error & { cause?: unknown }).cause = err?.cause;
        throw e;
      }
      return (await res.json()) as DryRunResult;
    },
  });
}

export function useUpdateAppointment(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      payload: Partial<{
        appointmentDate: number;
        status: AppointmentRow['status'];
        notes: string | null;
        agentProfileId: string | null;
        appointmentType: AppointmentRow['appointment_type'];
        propertyId: string | null;
      }>,
    ) => {
      const res = await apiFetch(`/appointments/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = (await res.json().catch(() => null)) as { error?: string } | null;
        throw new Error(err?.error ?? `appointment_update_failed_${res.status}`);
      }
      return (await res.json()) as AppointmentRow;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['appointments'] }),
  });
}

export function useArchiveAppointment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiFetch(`/appointments/${id}`, { method: 'DELETE' });
      if (!res.ok) {
        const j = (await res.json().catch(() => null)) as { error?: string } | null;
        throw new Error(j?.error ?? `appointment_archive_failed_${res.status}`);
      }
      return (await res.json()) as { ok: true; archived: true };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['appointments'] }),
  });
}

// ───── Multi-agent support ─────
export interface AppointmentAgentRow {
  id: string;
  appointment_id: string;
  agent_profile_id: string;
  role: 'primary' | 'secondary' | 'call_center';
  assigned_by: string | null;
  assigned_at: number;
}

export function useAppointmentAgents(appointmentId: string | undefined) {
  return useQuery({
    queryKey: ['appointment', appointmentId, 'agents'] as const,
    queryFn: async () => {
      const res = await apiFetch(`/appointments/${appointmentId}/agents`);
      if (!res.ok) throw new Error(`appt_agents_failed_${res.status}`);
      const json = (await res.json()) as { items: AppointmentAgentRow[] };
      return json.items;
    },
    enabled: !!appointmentId,
  });
}

export function useAddAppointmentAgent(appointmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      agentProfileId,
      role,
    }: {
      agentProfileId: string;
      role?: 'primary' | 'secondary' | 'call_center';
    }) => {
      const res = await apiFetch(`/appointments/${appointmentId}/agents`, {
        method: 'POST',
        body: JSON.stringify({ agentProfileId, role }),
      });
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(j.message ?? `add_agent_failed_${res.status}`);
      }
      return (await res.json()) as AppointmentAgentRow;
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['appointment', appointmentId, 'agents'] }),
  });
}

export function useRemoveAppointmentAgent(appointmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (agentProfileId: string) => {
      const res = await apiFetch(
        `/appointments/${appointmentId}/agents/${agentProfileId}`,
        { method: 'DELETE' },
      );
      if (!res.ok) throw new Error(`remove_agent_failed_${res.status}`);
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['appointment', appointmentId, 'agents'] }),
  });
}
